import { useMemo, useState } from 'react';
import { ActionSheet, ActionSheetItem } from '@vkontakte/vkui';
import TableScheme from './TableScheme';
import GuestAvatar from './GuestAvatar';
import TableWarnings from './TableWarnings';
import { buildUnits } from '../../utils/companions';
import type { Warning } from '../../utils/seatingWarnings';
import type { SeatingTable, Guest } from '../../types';

const TYPE_LABEL: Record<string, string> = {
  head: 'Президиум',
  round: 'Круглый стол',
  square: 'Квадратный стол',
  rectangular: 'Прямоугольный стол',
};

const MAX_AVATARS = 4;

interface TableCardProps {
  table: SeatingTable;
  guestsAtTable: Guest[];
  warnings: Warning[];
  onEdit: (t: SeatingTable) => void;
  onDelete: (t: SeatingTable) => void;
  onOpenGuests: (t: SeatingTable) => void;
  onReassignPairs: (t: SeatingTable) => void;
}

export default function TableCard({ table, guestsAtTable, warnings, onEdit, onDelete, onOpenGuests, onReassignPairs }: TableCardProps) {
  const [menu, setMenu] = useState(false);
  const [anchor, setAnchor] = useState<Element | null>(null);
  const filled = guestsAtTable.length;
  const cap = table.seats;
  const pct = cap > 0 ? Math.min(100, (filled / cap) * 100) : 0;
  const barClass = filled > cap ? ' st-bar__i--over' : filled === cap && cap > 0 ? ' st-bar__i--full' : '';

  const { ordered, pairIds } = useMemo(() => {
    const units = buildUnits(guestsAtTable);
    const ids = new Set<string>();
    for (const u of units) if (u.length === 2) u.forEach((g) => ids.add(g.id));
    return { ordered: units.flat(), pairIds: ids };
  }, [guestsAtTable]);

  const visible = ordered.slice(0, MAX_AVATARS);
  const rest = filled - visible.length;

  return (
    <div className="st-card" id={`st-table-${table.id}`}>
      <div className="st-card__row">
        <TableScheme type={table.type} seats={cap} filled={filled} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="st-card__name">{table.name}</div>
          <div className="st-card__meta">
            {TYPE_LABEL[table.type] || table.type} · №{table.position}
          </div>
        </div>
        <div className="st-card__cnt">
          <b>{filled}</b>
          <span>/{cap}</span>
        </div>
        <TableWarnings
          table={table}
          warnings={warnings}
          onReassignPairs={onReassignPairs}
          onOpenGuests={onOpenGuests}
        />
        <button
          type="button"
          className="st-dots"
          aria-label="Действия стола"
          ref={(el) => setAnchor(el)}
          onClick={() => setMenu(true)}
        >
          ⋯
        </button>
      </div>

      <div className="st-bar">
        <i className={`st-bar__i${barClass}`} style={{ width: `${pct}%` }} />
      </div>

      <div className="st-guests">
        {filled === 0 ? (
          <span className="st-muted" style={{ fontSize: 12 }}>Пусто</span>
        ) : (
          <div className="st-avatars">
            {visible.map((g) => (
              <GuestAvatar
                key={g.id}
                id={g.id}
                first_name={g.first_name}
                last_name={g.last_name}
                pair={pairIds.has(g.id)}
              />
            ))}
            {rest > 0 && <span className="st-av st-av--more">+{rest}</span>}
          </div>
        )}
        <button type="button" className="st-link" onClick={() => onOpenGuests(table)}>
          Гости ›
        </button>
      </div>

      {menu && (
        <ActionSheet
          mode="sheet"
          title={table.name}
          toggleRef={anchor}
          onClosed={() => setMenu(false)}
          iosCloseItem={<ActionSheetItem mode="cancel">Отмена</ActionSheetItem>}
        >
          <ActionSheetItem onClick={() => onEdit(table)}>Редактировать</ActionSheetItem>
          <ActionSheetItem mode="destructive" onClick={() => onDelete(table)}>
            Удалить
          </ActionSheetItem>
        </ActionSheet>
      )}
    </div>
  );
}
